import { useState } from 'react'
import FindDialog from './FindDialog'
import BackButton from './navigation/BackButton'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/useAuth'
import { useRkDecksEnabled } from './rkdecks/api'
import { NolosMark, NolosWordmark } from '../design/Logo'
import EileAssistant from './EileAssistant'
import ConsoleHost, { ConsoleRailEntry } from './console/ConsoleHost'
import ShortcutsSheet from './keyboard/ShortcutsSheet'
import ExplainToggle from './guidance/ExplainToggle'
import RouteErrorBoundary from './RouteErrorBoundary'
import { pageNameFor } from './routeError'
import '../design/fonts.css'
import '../design/tokens.css'
import './v2.css'
import ReviewComments from './reviewComments/ReviewOverlay'

/* v2 shell — skeleton stage: icon rail + surface outlet. High level first
 * (Ale 2026-06-10): structure is reviewed before any pane gets dense. */
export default function V2Shell() {
  const { user, logout } = useAuth()
  const [finding, setFinding] = useState(false)
  const role = user?.role
  // RK deck surface (W4) — the nav entry exists only while the rk_decks
  // namespace answers (dark ship: flag off = no probe hit = no link).
  const rkDecks = useRkDecksEnabled()
  // B6 audit (2026-09-04): the routed page draws inside its own error
  // boundary, keyed on the pathname so leaving the page resets it. A page
  // that throws shows the designed fallback in the outlet slot; the rail
  // above stays a door. EILE has its own, compact one — a panel failure
  // must never read as a page failure, and must never hide behind one.
  const { pathname, search, hash } = useLocation()
  const initials = (user?.username ?? '?')
    .split(/\s+/)
    .map((w: string) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <ConsoleHost>
      <div className="v2">
        <nav className="rail">
          <div className="brand">
            <NolosMark size={26} />
            <NolosWordmark className="wm" />
          </div>
          {pathname !== '/v2' || search || hash ? <BackButton className="rail-back" /> : null}
          <NavLink to="/v2" end title="Today">
            <svg viewBox="0 0 24 24">
              <path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z" />
            </svg>
            <span>Today</span>
          </NavLink>
          {/* W6: with the flag on the officer's entry point is the four-line
            workbench; with it off the nav is unchanged (the old Tasks
            entry, same icon, same position). */}
          {rkDecks === true ? (
            <NavLink to="/v2/workbench" title="Workbench">
              <svg viewBox="0 0 24 24">
                <path d="M4 6h16M4 12h16M4 18h10" />
              </svg>
              <span>Workbench</span>
            </NavLink>
          ) : (
            <NavLink to="/v2/tasks" title="Tasks">
              <svg viewBox="0 0 24 24">
                <path d="M4 6h16M4 12h16M4 18h10" />
              </svg>
              <span>Tasks</span>
            </NavLink>
          )}
          <NavLink to="/v2/funds" title="Funds">
            <svg viewBox="0 0 24 24">
              <path d="M3 7l3-3h5l2 2h8v13H3z" />
            </svg>
            <span>Funds</span>
          </NavLink>
          <NavLink to="/v2/traces" title="Traces">
            <svg viewBox="0 0 24 24">
              <path d="M7 3h7l5 5v13H7z M14 3v5h5" />
            </svg>
            <span>Traces</span>
          </NavLink>
          <NavLink to="/v2/manual" title="Manual entry">
            <svg viewBox="0 0 24 24">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>Manual entry</span>
          </NavLink>
          {/* Admin surfaces are role-gated: officers see none, a manager gets
            Policy, an admin gets the full Admin section. All hosted in this v2
            shell (no v1 back-door). */}
          {role === 'manager' && (
            <NavLink to="/v2/admin/policy" title="Policy">
              <svg viewBox="0 0 24 24">
                <path d="M12 8a4 4 0 100 8 4 4 0 000-8zM3 12h3M18 12h3M12 3v3M12 18v3" />
              </svg>
              <span>Policy</span>
            </NavLink>
          )}
          {role === 'admin' && (
            <NavLink to="/v2/admin" title="Admin">
              <svg viewBox="0 0 24 24">
                <path d="M12 8a4 4 0 100 8 4 4 0 000-8zM3 12h3M18 12h3M12 3v3M12 18v3" />
              </svg>
              <span>Admin</span>
            </NavLink>
          )}
          {/* Explain — under the nav, above the user: the help dots' switch
            (audit cut 3, Ale 2026-09-05). */}
          <button
            type="button"
            className="rail-find"
            onClick={() => setFinding(true)}
            title="Find by name"
          >
            <svg viewBox="0 0 24 24">
              <circle cx="10" cy="10" r="6" />
              <path d="m15 15 6 6" />
            </svg>
            <span>Find</span>
          </button>
          <ExplainToggle />
          <div className="me">
            <div className="av">{initials}</div>
            <span>{user?.username ?? 'Officer'}</span>
            <button
              type="button"
              onClick={() => {
                logout()
                window.location.href = '/login'
              }}
              title="Log out"
              aria-label="Log out"
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                opacity: 0.65,
                cursor: 'pointer',
                padding: '2px 4px',
                display: 'inline-flex',
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="15"
                height="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
            </button>
          </div>
          {/* Console · Client mode — BOTTOM of the sidebar (Ale 2026-11-01:
            the demo console lives inside the product). Renders only while
            /api/console answers (dark-flag probe) for a signed-in officer. */}
          <ConsoleRailEntry />
        </nav>
        <RouteErrorBoundary key={pathname} page={pageNameFor(pathname)}>
          <Outlet />
        </RouteErrorBoundary>
        <RouteErrorBoundary page="EILE" variant="panel">
          <EileAssistant />
        </RouteErrorBoundary>
        {/* "?" — the keyboard canon's shortcuts sheet (Tier-1 A5). */}
        <ShortcutsSheet />
        <ReviewComments />
        {finding && <FindDialog onClose={() => setFinding(false)} />}
      </div>
    </ConsoleHost>
  )
}
